import { useCallback, useEffect, useRef, useState } from "react";
import {
  useRetrievePreferencesQuery,
  useUpdatePreferencesMutation,
} from "services/userService";

const STORAGE_KEY = "marmalade_exposure_row_order";

const useExposureRowOrder = () => {
  const {
    data: serverPreferences,
    isLoading: isLoadingPreferences,
    isSuccess: isPreferencesLoaded,
  } = useRetrievePreferencesQuery();
  const [updatePreferences] = useUpdatePreferencesMutation();

  const [exposureRowOrderMap, setExposureRowOrderMap] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const hasSyncedFromServer = useRef(false);

  useEffect(() => {
    if (
      isPreferencesLoaded &&
      serverPreferences &&
      !hasSyncedFromServer.current
    ) {
      const serverOrder =
        serverPreferences.exposureRowOrder ||
        serverPreferences.exposure_row_order ||
        {};
      setExposureRowOrderMap(serverOrder);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(serverOrder));
      } catch {
        // Ignore localStorage errors
      }
      hasSyncedFromServer.current = true;
    }
  }, [isPreferencesLoaded, serverPreferences]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(exposureRowOrderMap));
    } catch {
      // Ignore localStorage errors
    }
  }, [exposureRowOrderMap]);

  const saveTimeoutRef = useRef(null);
  const serverPreferencesRef = useRef(serverPreferences);
  useEffect(() => {
    serverPreferencesRef.current = serverPreferences;
  }, [serverPreferences]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const saveToBackend = useCallback(
    (newOrder) => {
      if (!isPreferencesLoaded) return;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        updatePreferences({
          ...(serverPreferencesRef.current || {}),
          exposure_row_order: newOrder,
        }).catch(() => {});
      }, 500);
    },
    [isPreferencesLoaded, updatePreferences]
  );

  const getRowOrderForSubmission = useCallback(
    (submissionId) => {
      return exposureRowOrderMap[submissionId] || null;
    },
    [exposureRowOrderMap]
  );

  const setRowOrderForSubmission = useCallback(
    (submissionId, rowOrder) => {
      setExposureRowOrderMap((prev) => {
        const updatedMap = {
          ...prev,
          [submissionId]: rowOrder,
        };
        saveToBackend(updatedMap);
        return updatedMap;
      });
    },
    [saveToBackend]
  );

  return {
    exposureRowOrderMap,
    getRowOrderForSubmission,
    setRowOrderForSubmission,
    isLoading: isLoadingPreferences,
  };
};

export default useExposureRowOrder;
